'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { OrderCsv } from '@tagadapay/crm-entities';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SelectableChips, type SelectableChipsOption } from '@/components/ui/selectable-chips';
import { useToast } from '@/components/ui/use-toast';

import { useExportOrdersCsv } from '@/lib/api/orders';

const ROW_CAP = 10_000;

function buildInitialRange(): DateRange {
  const now = new Date();
  return { from: subDays(now, 30), to: now };
}

function triggerCsvDownload(filename: string, content: string) {
  const blob = new Blob(['﻿', content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

interface ExportOrdersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportOrdersDialog({ open, onOpenChange }: ExportOrdersDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const { toast } = useToast();

  const [range, setRange] = React.useState<DateRange>(buildInitialRange);
  const [fields, setFields] = React.useState<OrderCsv.OrderCsvField[]>(() => [
    ...OrderCsv.ORDER_CSV_FIELDS_ESSENTIALS,
  ]);

  const mutation = useExportOrdersCsv();

  const fieldOptions = React.useMemo<SelectableChipsOption<OrderCsv.OrderCsvField>[]>(
    () =>
      OrderCsv.ORDER_CSV_FIELDS.map((value) => ({
        value,
        label: t(`orders:exportDialog.fields.${value}`),
      })),
    [t],
  );

  const allSelected = fields.length === fieldOptions.length;
  const noneSelected = fields.length === 0;
  const isEssentials =
    fields.length === OrderCsv.ORDER_CSV_FIELDS_ESSENTIALS.length &&
    OrderCsv.ORDER_CSV_FIELDS_ESSENTIALS.every((v) => fields.includes(v));

  const canSubmit = Boolean(range.from && range.to) && fields.length > 0 && !mutation.isPending;

  const handleSubmit = () => {
    if (!range.from || !range.to) return;
    mutation.mutate(
      { from: range.from, to: range.to, fields },
      {
        onSuccess: ({ filename, content }) => {
          triggerCsvDownload(filename, content);
          toast({ description: t('orders:exportDialog.success') });
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('orders:exportDialog.title')}</DialogTitle>
          <DialogDescription>{t('orders:exportDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {t('orders:exportDialog.dateRangeLabel')}
            </label>
            <DateRangePicker value={range} onChange={setRange} />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">{t('orders:exportDialog.fieldsLabel')}</label>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFields([...OrderCsv.ORDER_CSV_FIELDS_ESSENTIALS])}
                  disabled={isEssentials || mutation.isPending}
                >
                  {t('orders:exportDialog.essentials')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFields([...OrderCsv.ORDER_CSV_FIELDS])}
                  disabled={allSelected || mutation.isPending}
                >
                  {t('orders:exportDialog.selectAll')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFields([])}
                  disabled={noneSelected || mutation.isPending}
                >
                  {t('orders:exportDialog.clear')}
                </Button>
              </div>
            </div>
            <SelectableChips<OrderCsv.OrderCsvField>
              options={fieldOptions}
              selected={fields}
              onChange={setFields}
              disabled={mutation.isPending}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {t('orders:exportDialog.rowCapHint', { cap: ROW_CAP.toLocaleString() })}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t('orders:exportDialog.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {t('orders:exportDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
